import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '@/i18n/translations';
import { Helmet } from 'react-helmet';
import { 
  QrCode, ListChecks, Users, ArrowRight, Sparkles, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  // Redirect to dashboard if authenticated
  if (!loading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="animate-pulse text-neon-cyan">{t('common', 'loading')}</div>
      </div>
    );
  }

  // FAQ items with translations
  const faqItems = {
    fr: [
      {
        question: "Les invités doivent-ils télécharger une application ?",
        answer: "Non. Les invités scannent simplement votre QR code et accèdent à la page de demande via leur navigateur. Aucun téléchargement n'est nécessaire."
      },
      {
        question: "Est-ce gratuit ?",
        answer: "Oui. TrackVibes propose une version gratuite permettant de recevoir des demandes en live via QR code. Une version Pro est également disponible pour les DJs souhaitant accéder à des fonctionnalités avancées."
      },
      {
        question: "Y a-t-il de la publicité dans l'application ?",
        answer: "Non. TrackVibes n'affiche aucune publicité, ni pour les DJs ni pour les invités. L'expérience reste professionnelle et fluide."
      },
      {
        question: "Puis-je refuser une demande ?",
        answer: "Oui. Vous gardez le contrôle total : vous pouvez accepter ou refuser une demande, avec un motif visible par l'invité."
      }
    ],
    en: [
      {
        question: "Do guests need to download an app?",
        answer: "No. Guests simply scan your QR code and access the request page via their browser. No download required."
      },
      {
        question: "Is it free?",
        answer: "Yes. TrackVibes offers a free version to receive live requests via QR code. A Pro version is also available for advanced features."
      },
      {
        question: "Is there advertising in the app?",
        answer: "No. TrackVibes does not display any advertising, neither for DJs nor for guests. The experience remains professional and smooth."
      },
      {
        question: "Can I refuse a request?",
        answer: "Yes. You have full control: you can accept or refuse a request, with a reason visible to the guest."
      }
    ],
    es: [
      {
        question: "¿Los invitados necesitan descargar una aplicación?",
        answer: "No. Los invitados simplemente escanean tu código QR y acceden a la página de solicitud a través de su navegador. No se requiere descarga."
      },
      {
        question: "¿Es gratis?",
        answer: "Sí. TrackVibes ofrece una versión gratuita para recibir solicitudes en vivo mediante código QR. También está disponible una versión Pro para funciones avanzadas."
      },
      {
        question: "¿Hay publicidad en la aplicación?",
        answer: "No. TrackVibes no muestra publicidad, ni para DJs ni para invitados. La experiencia sigue siendo profesional y fluida."
      },
      {
        question: "¿Puedo rechazar una solicitud?",
        answer: "Sí. Tienes control total: puedes aceptar o rechazar una solicitud, con un motivo visible para el invitado."
      }
    ],
    de: [
      {
        question: "Müssen Gäste eine App herunterladen?",
        answer: "Nein. Gäste scannen einfach Ihren QR-Code und greifen über ihren Browser auf die Anfrage-Seite zu. Kein Download erforderlich."
      },
      {
        question: "Ist es kostenlos?",
        answer: "Ja. TrackVibes bietet eine kostenlose Version, um Live-Anfragen per QR-Code zu erhalten. Eine Pro-Version ist auch für erweiterte Funktionen verfügbar."
      },
      {
        question: "Gibt es Werbung in der App?",
        answer: "Nein. TrackVibes zeigt keine Werbung an, weder für DJs noch für Gäste. Das Erlebnis bleibt professionell und flüssig."
      },
      {
        question: "Kann ich eine Anfrage ablehnen?",
        answer: "Ja. Sie haben die volle Kontrolle: Sie können eine Anfrage annehmen oder ablehnen, mit einem für den Gast sichtbaren Grund."
      }
    ],
    pt: [
      {
        question: "Os convidados precisam baixar um aplicativo?",
        answer: "Não. Os convidados simplesmente escaneiam seu código QR e acessam a página de solicitação pelo navegador. Nenhum download necessário."
      },
      {
        question: "É gratuito?",
        answer: "Sim. TrackVibes oferece uma versão gratuita para receber pedidos ao vivo via código QR. Uma versão Pro também está disponível para recursos avançados."
      },
      {
        question: "Há publicidade no aplicativo?",
        answer: "Não. TrackVibes não exibe publicidade, nem para DJs nem para convidados. A experiência permanece profissional e fluida."
      },
      {
        question: "Posso recusar um pedido?",
        answer: "Sim. Você tem controle total: pode aceitar ou recusar um pedido, com um motivo visível para o convidado."
      }
    ]
  };

  const currentFaq = faqItems[language] || faqItems.fr;

  // "How it works" translations
  const howItWorksTitle = {
    fr: 'Comment ça marche',
    en: 'How it works',
    es: 'Cómo funciona',
    de: 'So funktioniert es',
    pt: 'Como funciona'
  };

  const howItWorksSteps = {
    fr: ['Crée ton compte DJ', 'Génère ton QR code', 'Reçois les demandes en live'],
    en: ['Create your DJ account', 'Generate your QR code', 'Receive live requests'],
    es: ['Crea tu cuenta DJ', 'Genera tu código QR', 'Recibe solicitudes en vivo'],
    de: ['Erstelle dein DJ-Konto', 'Generiere deinen QR-Code', 'Empfange Live-Anfragen'],
    pt: ['Crie sua conta DJ', 'Gere seu QR code', 'Receba pedidos ao vivo']
  };

  // "For who" translations
  const forWhoTitle = {
    fr: 'Pour quels événements',
    en: 'For what events',
    es: 'Para qué eventos',
    de: 'Für welche Events',
    pt: 'Para quais eventos'
  };

  const forWhoTags = {
    fr: ['Mariages', 'Bars & Clubs', 'Soirées privées'],
    en: ['Weddings', 'Bars & Clubs', 'Private parties'],
    es: ['Bodas', 'Bares & Clubs', 'Fiestas privadas'],
    de: ['Hochzeiten', 'Bars & Clubs', 'Private Feiern'],
    pt: ['Casamentos', 'Bares & Clubs', 'Festas privadas']
  };

  // Footer translations
  const footerLinks = {
    fr: { privacy: 'Confidentialité', terms: 'CGU', legal: 'Mentions légales', contact: 'Contact' },
    en: { privacy: 'Privacy', terms: 'Terms', legal: 'Legal notice', contact: 'Contact' },
    es: { privacy: 'Privacidad', terms: 'Términos', legal: 'Aviso legal', contact: 'Contacto' },
    de: { privacy: 'Datenschutz', terms: 'AGB', legal: 'Impressum', contact: 'Kontakt' },
    pt: { privacy: 'Privacidade', terms: 'Termos', legal: 'Aviso legal', contact: 'Contato' }
  };

  const currentSteps = howItWorksSteps[language] || howItWorksSteps.fr;
  const currentTags = forWhoTags[language] || forWhoTags.fr;
  const currentFooter = footerLinks[language] || footerLinks.fr;

  return (
    <>
      <Helmet>
        {/* SEO Meta - updated for better indexing */}
        <title>TrackVibes – Système de demandes musicales pour DJ avec QR code</title>
        <meta name="description" content="TrackVibes permet aux invités de demander et voter pour des musiques pendant les soirées DJ via un QR code. Idéal pour mariages, soirées privées, bars et clubs." />
        <meta name="keywords" content="demande musique DJ, application DJ demandes musique, QR code DJ musique, demandes musique mariage DJ, DJ song request app" />
        <link rel="canonical" href="https://app.trackvibes.app/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="TrackVibes – Système de demandes musicales pour DJ avec QR code" />
        <meta property="og:description" content="TrackVibes permet aux invités de demander et voter pour des musiques pendant les soirées DJ via un QR code. Idéal pour mariages, soirées privées, bars et clubs." />
        <meta property="og:image" content="https://app.trackvibes.app/assets/trackvibes-og-image.png" />
        <meta property="og:url" content="https://app.trackvibes.app/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TrackVibes – Système de demandes musicales pour DJ avec QR code" />
        <meta name="twitter:description" content="TrackVibes permet aux invités de demander et voter pour des musiques pendant les soirées DJ via un QR code." />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a14] text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a14]/95 backdrop-blur-md border-b border-white/5">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/assets/trackvibes-logo.png" 
                alt="TrackVibes" 
                className="h-10"
              />
            </Link>
            <nav className="flex items-center gap-3">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                    <Globe className="w-4 h-4 mr-1" />
                    {LANGUAGE_FLAGS[language]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#1a1a1a] border-white/10">
                  {['fr', 'en', 'es', 'de', 'pt'].map((lang) => (
                    <DropdownMenuItem 
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      className="text-white hover:bg-white/10 cursor-pointer"
                    >
                      {LANGUAGE_FLAGS[lang]} {LANGUAGE_NAMES[lang]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to="/login">
                <Button variant="ghost" className="text-gray-300 hover:text-white text-sm">
                  {t('common', 'login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-neon-cyan text-black font-medium hover:bg-neon-cyan/90 rounded-full px-5 text-sm">
                  {t('common', 'register')}
                </Button>
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-36 pb-24 px-6">
          <div className="max-w-3xl mx-auto text-center">
            {/* Badge sobre */}
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-10">
              <QrCode className="w-4 h-4 text-neon-cyan" />
              <span className="text-sm text-gray-300">{t('landing', 'feature_qr_title')}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-heading font-bold leading-tight mb-8 tracking-tight">
              {t('landing', 'hero_title')}<br />
              <span className="text-neon-cyan">QR code</span>
            </h1>
            
            <p className="text-lg text-gray-400 max-w-xl mx-auto mb-12 leading-relaxed">
              {t('landing', 'hero_subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-neon-cyan text-black font-semibold text-base px-8 py-6 rounded-full hover:bg-neon-cyan/90 transition-all min-w-[220px]">
                  {t('landing', 'cta_start')}
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-6 rounded-full min-w-[180px]">
                  {t('common', 'login')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 px-6 bg-white/[0.01]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold mb-4">
                {t('landing', 'features_title')}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: QrCode,
                  title: t('landing', 'feature_qr_title'),
                  desc: t('landing', 'feature_qr_desc')
                },
                {
                  icon: ListChecks,
                  title: t('landing', 'feature_realtime_title'),
                  desc: t('landing', 'feature_realtime_desc')
                },
                {
                  icon: Users,
                  title: t('landing', 'feature_events_title'),
                  desc: t('landing', 'feature_events_desc')
                }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-8">
                  <div className="w-14 h-14 bg-neon-cyan/10 border border-neon-cyan/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold mb-4">
                {howItWorksTitle[language] || howItWorksTitle.fr}
              </h2>
            </div>
            
            <div className="space-y-6">
              {currentSteps.map((title, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-6 bg-white/[0.02] border border-white/5 rounded-2xl p-6"
                >
                  <div className="w-12 h-12 bg-neon-cyan/10 border border-neon-cyan/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-neon-cyan font-semibold">{idx + 1}</span>
                  </div>
                  <span className="text-lg">{title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For who - Tags */}
        <section className="py-20 px-6 bg-white/[0.01]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold mb-10">
              {forWhoTitle[language] || forWhoTitle.fr}
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              {currentTags.map((tag, idx) => (
                <span 
                  key={idx} 
                  className="bg-white/5 border border-white/10 text-gray-300 px-6 py-3 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold mb-4">
                FAQ
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {currentFaq.map((item, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`item-${idx}`}
                  className="bg-white/[0.02] border border-white/10 rounded-xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left text-base font-medium py-5 hover:no-underline text-white">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* CTA after FAQ */}
            <div className="mt-16 text-center">
              <Link to="/register">
                <Button size="lg" className="bg-neon-cyan text-black font-semibold text-base px-10 py-6 rounded-full hover:bg-neon-cyan/90 transition-all w-full sm:w-auto">
                  {t('landing', 'cta_start')}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 px-6 bg-white/[0.01]">
          <div className="max-w-2xl mx-auto text-center">
            <Sparkles className="w-8 h-8 text-neon-cyan mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold mb-6">
              {t('landing', 'cta_start')}
            </h2>
            <p className="text-gray-400 mb-10">
              {t('landing', 'hero_subtitle')}
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-neon-cyan text-black font-semibold text-base px-10 py-6 rounded-full hover:bg-neon-cyan/90 transition-all">
                {t('common', 'register')}
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-12 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <img src="/assets/trackvibes-logo.png" alt="TrackVibes" className="h-8" />
                <span className="text-gray-500 text-sm">© 2026 TrackVibes</span>
              </div>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                <Link to="/privacy" className="hover:text-white transition-colors">{currentFooter.privacy}</Link>
                <Link to="/conditions" className="hover:text-white transition-colors">{currentFooter.terms}</Link>
                <Link to="/mentions-legales" className="hover:text-white transition-colors">{currentFooter.legal}</Link>
                <a href="mailto:contact@trackvibes.app" className="hover:text-white transition-colors">{currentFooter.contact}</a>
              </nav>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
